import { ChevronUp, ChevronDown, ExternalLink, Trash2 } from 'lucide-react';
import type { MouseEvent } from 'react';
import type { PbiReportRow, PbiSortField, SortOrder } from '../../types/powerBiUsage';
import { powerBiReportUrl } from '../../types/powerBiUsage';
import { PBI_REVIEW_META, toReviewStatus } from './status';
import { Sparkline } from './Sparkline';
import { fmtNum, fmtDate, lastViewBadgeClass } from './format';

interface Props {
    items: PbiReportRow[];
    sortBy: PbiSortField;
    sortOrder: SortOrder;
    onSort: (field: PbiSortField) => void;
    onSelect: (row: PbiReportRow) => void;
    selectedId: string | null;
    compact: boolean;
    canEdit: boolean;
    onStatusClick: (row: PbiReportRow, e: MouseEvent) => void;
    onRowContextMenu: (row: PbiReportRow, e: MouseEvent) => void;
}

interface Col {
    field: PbiSortField | null;
    label: string;
    className?: string;
    hideCompact?: boolean;
}

const COLS: Col[] = [
    { field: 'name', label: 'Report' },
    { field: 'workspaceName', label: 'Workspace', hideCompact: true },
    { field: 'views', label: 'Views', className: 'text-right' },
    { field: 'distinctUsers', label: 'Users', className: 'text-right' },
    { field: 'lastViewedAt', label: 'Last viewed' },
    { field: null, label: 'Trend', hideCompact: true },
    { field: null, label: 'Review' },
    { field: null, label: '' },
];

/**
 * Report table. Left-click = detail drawer; click the review cell = status menu;
 * right-click = context menu (handled by the page). The external-link cell opens the
 * report in the Power BI service and must not select the row (stopPropagation).
 */
export function PowerBiUsageTable({
    items, sortBy, sortOrder, onSort, onSelect, selectedId, compact, canEdit,
    onStatusClick, onRowContextMenu,
}: Props) {
    const cols = COLS.filter(c => !(compact && c.hideCompact));

    if (items.length === 0) {
        return (
            <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
                No reports match the current filters.
            </div>
        );
    }

    return (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                    <tr>
                        {cols.map(col => (
                            <th
                                key={col.label || 'link'}
                                scope="col"
                                className={`px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 ${col.className ?? ''}`}
                            >
                                {col.field ? (
                                    <button
                                        type="button"
                                        onClick={() => onSort(col.field as PbiSortField)}
                                        className="inline-flex items-center gap-1 hover:text-gray-700"
                                    >
                                        {col.label}
                                        {sortBy === col.field && (
                                            sortOrder === 'asc'
                                                ? <ChevronUp className="h-3 w-3" />
                                                : <ChevronDown className="h-3 w-3" />
                                        )}
                                    </button>
                                ) : col.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {items.map(row => {
                        const review = toReviewStatus(row.reviewStatus);
                        const meta = PBI_REVIEW_META[review];
                        const ReviewIcon = meta.icon;
                        const url = powerBiReportUrl(row);
                        const selected = selectedId === row.reportId;
                        return (
                            <tr
                                key={row.reportId}
                                onClick={() => onSelect(row)}
                                onContextMenu={e => { e.preventDefault(); onRowContextMenu(row, e); }}
                                className={`cursor-pointer transition hover:bg-blue-50/50 ${selected ? 'bg-blue-50' : ''}`}
                            >
                                <td className="max-w-[26rem] px-3 py-2">
                                    <div className="flex items-center gap-1.5 truncate font-medium text-gray-900">
                                        {row.isDeleted && <Trash2 className="h-3.5 w-3.5 shrink-0 text-red-400" />}
                                        <span className="truncate">{row.name}</span>
                                    </div>
                                    {compact && row.workspaceName && (
                                        <p className="truncate text-xs text-gray-500">{row.workspaceName}</p>
                                    )}
                                </td>
                                {!compact && (
                                    <td className="max-w-[16rem] truncate px-3 py-2 text-gray-600">
                                        {row.workspaceName ?? '—'}
                                        {row.workspaceType === 'PersonalGroup' && (
                                            <span className="ml-1 rounded bg-gray-100 px-1 text-[10px] text-gray-500">personal</span>
                                        )}
                                    </td>
                                )}
                                <td className="px-3 py-2 text-right tabular-nums text-gray-900">{fmtNum(row.views)}</td>
                                <td className="px-3 py-2 text-right tabular-nums text-gray-900">{fmtNum(row.distinctUsers)}</td>
                                <td className="px-3 py-2">
                                    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${lastViewBadgeClass(row.daysSinceLastView)}`}>
                                        {row.lastViewedAt
                                            ? `${fmtDate(row.lastViewedAt)} · ${row.daysSinceLastView}d`
                                            : 'Never'}
                                    </span>
                                </td>
                                {!compact && (
                                    <td className="px-3 py-2">
                                        <Sparkline values={row.trend} />
                                    </td>
                                )}
                                <td className="px-3 py-2">
                                    <button
                                        type="button"
                                        title={canEdit ? `Review: ${meta.label} — click to change` : `Review: ${meta.label}`}
                                        onClick={e => {
                                            e.stopPropagation();
                                            if (canEdit) onStatusClick(row, e);
                                        }}
                                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${meta.badgeClass} ${canEdit ? 'hover:ring-2 hover:ring-blue-200' : 'cursor-default'}`}
                                    >
                                        <ReviewIcon className={`h-3.5 w-3.5 ${meta.iconClass}`} />
                                        {meta.label}
                                    </button>
                                </td>
                                <td className="px-3 py-2">
                                    {url && (
                                        <a
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title="Open in Power BI"
                                            onClick={e => e.stopPropagation()}
                                            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-blue-600"
                                        >
                                            <ExternalLink className="h-4 w-4" />
                                        </a>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
